import React from 'react';

const CleanerPerformanceCard = ({ data, P }) => {
  if (!data) return null;

  const stats = data.response_stats || {};
  const vStats = data.verification_stats || {};
  const props = data.assigned_properties || [];
  const perProp = data.per_property || [];

  const acceptRate = stats.acceptance_rate_percent;
  const avgScore = vStats.average_score;
  const trend = vStats.scores_trend;
  const recentScores = vStats.recent_scores || [];

  // Health badge logic
  const isGreen = (acceptRate == null || acceptRate >= 70) && (avgScore == null || avgScore >= 80);
  const isRed = (acceptRate != null && acceptRate < 50) || (avgScore != null && avgScore < 60);
  const healthColor = isRed ? '#ef4444' : isGreen ? '#22c55e' : '#eab308';
  const healthLabel = isRed ? 'Needs Improvement' : isGreen ? 'Strong' : 'Watch';

  // Trend arrow
  const trendArrow = trend === 'improving' ? '↑' : trend === 'declining' ? '↓' : '→';
  const trendColor = trend === 'improving' ? '#22c55e' : trend === 'declining' ? '#ef4444' : P.textMuted;

  // Risk flags
  const risks = [];
  if (acceptRate != null && acceptRate < 50) risks.push('Low acceptance rate — may need reassignment or check-in');
  if (stats.no_reply > 0 && stats.total_requests > 0 && (stats.no_reply / stats.total_requests) > 0.3)
    risks.push('High no-reply rate — messages may not be reaching cleaner');
  if (trend === 'declining') risks.push('Verification score trend declining');

  const MetricBar = ({ value, max = 100, color }) => (
    <div style={{
      width: '100%', height: '6px', borderRadius: '3px',
      backgroundColor: P.border, marginTop: '4px',
    }}>
      <div style={{
        width: `${Math.min((value / max) * 100, 100)}%`, height: '100%',
        borderRadius: '3px', backgroundColor: color,
        transition: 'width 0.3s ease',
      }} />
    </div>
  );

  return (
    <div style={{
      borderRadius: '10px', border: `1px solid ${P.border}`,
      overflow: 'hidden', marginBottom: '12px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: `1px solid ${P.border}`,
        backgroundColor: P.tableHeader,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: P.textBright }}>
            {data.cleaner || 'Aynista'}
          </span>
          {props.length > 0 && (
            <span style={{ fontSize: '11px', color: P.textMuted }}>
              {props.length} propert{props.length === 1 ? 'y' : 'ies'}
            </span>
          )}
        </div>
        <span style={{
          fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px',
          backgroundColor: healthColor + '18', color: healthColor, letterSpacing: '0.3px',
        }}>
          {healthLabel}
        </span>
      </div>

      {/* Metrics grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: '1px', backgroundColor: P.border,
      }}>
        {/* Acceptance Rate */}
        <div style={{ padding: '10px 14px', backgroundColor: P.tableHeader === 'transparent' ? 'transparent' : P.bg || 'transparent' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: P.textMuted, marginBottom: '4px' }}>
            Acceptance Rate
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: acceptRate != null ? (acceptRate >= 70 ? '#22c55e' : acceptRate >= 50 ? '#eab308' : '#ef4444') : P.textMuted }}>
            {acceptRate != null ? `${acceptRate}%` : '—'}
          </div>
          {acceptRate != null && <MetricBar value={acceptRate} color={acceptRate >= 70 ? '#22c55e' : acceptRate >= 50 ? '#eab308' : '#ef4444'} />}
        </div>

        {/* Verification Score */}
        <div style={{ padding: '10px 14px', backgroundColor: P.tableHeader === 'transparent' ? 'transparent' : P.bg || 'transparent' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: P.textMuted, marginBottom: '4px' }}>
            Verification Score
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            <span style={{ fontSize: '18px', fontWeight: 700, color: avgScore != null ? (avgScore >= 80 ? '#22c55e' : avgScore >= 60 ? '#eab308' : '#ef4444') : P.textMuted }}>
              {avgScore != null ? avgScore : '—'}
            </span>
            {trend && (
              <span style={{ fontSize: '13px', fontWeight: 600, color: trendColor }}>
                {trendArrow}
              </span>
            )}
          </div>
          {avgScore != null && <MetricBar value={avgScore} color={avgScore >= 80 ? '#22c55e' : avgScore >= 60 ? '#eab308' : '#ef4444'} />}
        </div>

        {/* Request Breakdown */}
        <div style={{ padding: '10px 14px', backgroundColor: P.tableHeader === 'transparent' ? 'transparent' : P.bg || 'transparent' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: P.textMuted, marginBottom: '6px' }}>
            Requests
          </div>
          <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
            <span style={{ color: P.text }}><span style={{ fontWeight: 600, color: P.textBright }}>{stats.total_requests || 0}</span> sent</span>
            <span style={{ color: '#22c55e' }}>{stats.accepted || 0} yes</span>
            <span style={{ color: '#ef4444' }}>{stats.declined || 0} no</span>
          </div>
          {stats.no_reply > 0 && (
            <div style={{ fontSize: '11px', color: P.textMuted, marginTop: '2px' }}>
              {stats.no_reply} no reply
            </div>
          )}
        </div>

        {/* Recent Scores */}
        <div style={{ padding: '10px 14px', backgroundColor: P.tableHeader === 'transparent' ? 'transparent' : P.bg || 'transparent' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: P.textMuted, marginBottom: '6px' }}>
            Recent Scores
          </div>
          {recentScores.length > 0 ? (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
              {recentScores.map((s, i) => (
                <div key={i} style={{
                  width: '20px', textAlign: 'center',
                }}>
                  <div style={{
                    height: `${Math.max(s * 0.3, 6)}px`,
                    backgroundColor: s >= 80 ? '#22c55e' : s >= 60 ? '#eab308' : '#ef4444',
                    borderRadius: '2px 2px 0 0',
                    marginBottom: '2px',
                  }} />
                  <span style={{ fontSize: '9px', color: P.textMuted }}>{s}</span>
                </div>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '12px', color: P.textMuted }}>No scores yet</span>
          )}
        </div>
      </div>

      {/* Per-property breakdown */}
      {perProp.length > 0 && (
        <div style={{ borderTop: `1px solid ${P.border}` }}>
          <div style={{
            display: 'flex', gap: '12px', padding: '8px 14px',
            overflowX: 'auto', fontSize: '11px',
          }}>
            {perProp.map((pp, i) => (
              <span key={i} style={{ color: P.textMuted, whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 600, color: P.text }}>{pp.property}</span>
                {' '}{pp.accepts}/{pp.requests}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Risk callout */}
      {risks.length > 0 && (
        <div style={{
          margin: '0 10px 10px', padding: '8px 12px', borderRadius: '8px',
          backgroundColor: isRed ? 'rgba(239,68,68,0.06)' : 'rgba(234,179,8,0.06)',
          border: `1px solid ${isRed ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.15)'}`,
          fontSize: '12px', color: isRed ? '#ef4444' : '#b45309',
        }}>
          {risks.map((r, i) => (
            <div key={i} style={{ marginBottom: i < risks.length - 1 ? '4px' : 0 }}>
              {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CleanerPerformanceCard;
